/* ─────────────────────────────────────────────────────────────────────────────
   Zero invoice_value substitution — per-merchant config.

   Orders posted with invoice_value: 0 are rejected during request binding
   (@DecimalMin("0.01") on MongoOrder.invoiceValue). Merchants sending reverse /
   exchange pickups legitimately have no commercial invoice, so this config lets
   ProShip substitute a positive value at intake for an opted-in merchant.

   Forward and reverse are opted in SEPARATELY and on purpose: a zero on a
   reverse leg is usually correct, whereas a zero on a forward sale is almost
   always a missing price mapping upstream and should keep failing loudly.
   Leaving a direction blank means "keep rejecting that direction".

   Backend: POST /api/config/merchant/{id}/invoiceValueOnZero
   Read:    GET  /api/config/merchant/{id}  -> merchantConfig.invoiceValueOnZero
   ──────────────────────────────────────────────────────────────────────────── */
import * as React from 'react';
import {
    Card, CardContent, CardHeader, Grid, Typography, Box, Chip, Alert, Button
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { FormProvider, RHFTextField, RHFSwitch } from '../../../common/hook-form';
import { apiConstants, postGeneric, deleteGeneric } from '../../../common/constants';

/* '' / null / undefined -> null (means "this direction is not opted in").
   Anything else -> Number, so the backend sees a real number and not a string.

   0 also maps to null: RHFTextField with type="number" turns a cleared input into
   0 (`field.onChange(+event.target.value)`) and renders 0 back as blank, so a
   cleared field arrives here as 0, not ''. 0 is never a legitimate value anyway --
   the backend enforces @DecimalMin("0.01") -- so treating it as "cleared" is both
   safe and what the admin meant. Negatives are left alone so a typo still errors. */
const toValueOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isNaN(n) || n === 0) return null;
    return n;
};

const fmt = (v) => (v === null || v === undefined ? '—' : v);

export default function InvoiceValueOnZero({ id, config, setReload }) {
    const { enqueueSnackbar } = useSnackbar();
    const [isSaving, setIsSaving] = React.useState(false);

    const saved = config?.invoiceValueOnZero;

    const methods = useForm({
        defaultValues: { enabled: false, forwardValue: '', reverseValue: '' }
    });
    const { handleSubmit, reset, watch } = methods;

    const enabled = watch('enabled');
    const forwardValue = watch('forwardValue');

    /* Hydrate from the saved config once it arrives / changes. */
    React.useEffect(() => {
        reset({
            enabled: saved?.enabled ?? false,
            forwardValue: saved?.forwardValue ?? '',
            reverseValue: saved?.reverseValue ?? ''
        });
    }, [saved, reset]);

    const onSubmit = async (data) => {
        const forward = toValueOrNull(data.forwardValue);
        const reverse = toValueOrNull(data.reverseValue);

        /* Mirrors the server-side check so the admin gets the error inline
           instead of a 400 round-trip. */
        if (data.enabled && forward === null && reverse === null) {
            enqueueSnackbar(
                'Enable requires a value for at least one direction — otherwise nothing is substituted.',
                { variant: 'warning' }
            );
            return;
        }
        /* Backend rejects <= 0 (@DecimalMin 0.01); catch it before the request. */
        if ((forward !== null && forward <= 0) || (reverse !== null && reverse <= 0)) {
            enqueueSnackbar('Values must be greater than 0.', { variant: 'warning' });
            return;
        }

        setIsSaving(true);
        const payload = { enabled: data.enabled, forwardValue: forward, reverseValue: reverse };

        try {
            await postGeneric(`${apiConstants.MERCHANT_CONFIG}/${id}/invoiceValueOnZero`, payload);

            /* The save only evicts the merchant-config cache on the node that served
               the request; the cross-instance broadcast in CacheServiceImpl is
               commented out. Without this the change is invisible on the other prod
               nodes for up to the cache TTL (1 day), which reads as "I saved it and
               nothing happened". Reported separately so a cache failure is not
               mistaken for a failed save. */
            try {
                await deleteGeneric(`${apiConstants.GET_CACHE}configs/${id}`);
                enqueueSnackbar('Configuration saved', { variant: 'success' });
            } catch (cacheErr) {
                console.error(cacheErr);
                enqueueSnackbar(
                    'Saved, but the config cache could not be cleared — it may take up to 24h to take effect on all servers.',
                    { variant: 'warning', autoHideDuration: 8000 }
                );
            }

            if (setReload) setReload((r) => r + 1);
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                err?.response?.data?.message || 'Some error occurred',
                { variant: 'error' }
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card sx={{ mb: 2 }}>
            <CardHeader
                title={
                    <Typography variant="h6" component="span">
                        Zero Invoice Value Substitution
                        <Box component="span" sx={{ ml: 1.5 }}>
                            <Chip
                                size="small"
                                label={saved?.enabled ? 'Enabled' : 'Disabled'}
                                color={saved?.enabled ? 'success' : 'default'}
                            />
                        </Box>
                        {saved?.enabled && (
                            <Box component="span" sx={{ ml: 1.5, display: 'inline-block' }}>
                                <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                    Current:{' '}
                                </Typography>
                                <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    Forward {fmt(saved?.forwardValue)} · Reverse {fmt(saved?.reverseValue)}
                                </Typography>
                            </Box>
                        )}
                    </Typography>
                }
                subheader="When an order arrives with invoice_value = 0, substitute the configured value instead of rejecting it."
            />
            <CardContent>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12}>
                            <RHFSwitch
                                name="enabled"
                                label="Enabled"
                                helperText="Off means orders with invoice_value = 0 keep getting rejected, as they do today."
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <RHFTextField
                                name="reverseValue"
                                label="Reverse value"
                                type="number"
                                disabled={!enabled}
                                inputProps={{ step: '0.01', min: '0.01' }}
                                helperText="Reverse / exchange pickups. Blank = keep rejecting reverse orders."
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <RHFTextField
                                name="forwardValue"
                                label="Forward value"
                                type="number"
                                disabled={!enabled}
                                inputProps={{ step: '0.01', min: '0.01' }}
                                helperText="Blank = keep rejecting forward orders. See warning below before setting."
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LoadingButton
                                loading={isSaving}
                                size="small"
                                variant="contained"
                                color="primary"
                                type="submit"
                            >
                                Save
                            </LoadingButton>
                            <Button
                                size="small"
                                variant="contained"
                                color="error"
                                sx={{ ml: 1 }}
                                onClick={() =>
                                    reset({
                                        enabled: saved?.enabled ?? false,
                                        forwardValue: saved?.forwardValue ?? '',
                                        reverseValue: saved?.reverseValue ?? ''
                                    })
                                }
                            >
                                Reset
                            </Button>
                        </Grid>

                        {enabled && toValueOrNull(forwardValue) !== null && (
                            <Grid item xs={12}>
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    <strong>Forward substitution is set.</strong> A zero invoice value on a
                                    forward sale is almost always a missing price mapping in the merchant's
                                    system, not a genuine free shipment. Substituting hides that, and the
                                    declared value carried to the courier drives claim payout and e-way-bill
                                    thresholds — under-declaring a real sale caps what can be recovered if the
                                    shipment is lost. Prefer leaving this blank and fixing the upstream feed.
                                </Alert>
                            </Grid>
                        )}

                        {enabled && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Substitutions are recorded on the order under{' '}
                                    <code>additionalData.invoice_value_on_zero</code>.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </FormProvider>
            </CardContent>
        </Card>
    );
}
